import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Alert,
  CircularProgress,
  TextField,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { 
  Edit as EditIcon, 
  Visibility as ViewIcon, 
  Search as SearchIcon,
  Settings as SettingsIcon 
} from '@mui/icons-material';
import api from '../../services/api';

interface GeneralInquiry {
  id: number;
  name: string;
  user_id: number | null;
  email: string;
  message: string;
  status: number;
  created_at: string;
  updated_at: string;
}

interface StatusUpdateRequest {
  status: number;
}

const GeneralInquiryTab: React.FC = () => {
  const [inquiries, setInquiries] = useState<GeneralInquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<number>(-1); // -1 means all statuses
  const searchTermRef = useRef(searchTerm);
  
  // Status update dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState<GeneralInquiry | null>(null);
  const [newStatus, setNewStatus] = useState<number>(0);
  
  // View details dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewInquiry, setViewInquiry] = useState<GeneralInquiry | null>(null);

  // Update ref when searchTerm changes
  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  const statusLabels = {
    0: { label: 'Pending', color: 'warning' as const },
    1: { label: 'In Progress', color: 'info' as const },
    2: { label: 'Completed', color: 'success' as const },
    3: { label: 'Rejected', color: 'error' as const },
    4: { label: 'On Hold', color: 'default' as const },
    5: { label: 'Canceled', color: 'default' as const },
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = () => {
    setPage(1); // Reset to first page when submitting search
    fetchInquiries();
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setStatusFilter(value === 'all' ? -1 : parseInt(value));
    setPage(1); // Reset to first page when filtering
  };

  const fetchInquiries = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      params.append('filter', 'general'); // Exclude OAPI inquiries
      
      if (statusFilter !== -1) {
        params.append('status', statusFilter.toString());
      }
      
      if (searchTermRef.current.trim()) {
        params.append('search', searchTermRef.current.trim());
      }
      
      const queryString = params.toString();
      console.log('Search params:', queryString);
      console.log('Search term:', searchTermRef.current);
      console.log('Status filter:', statusFilter);
      
      const response = await api.get(`/api/inquiry/general?${queryString}`);
      console.log('Response:', response.data);
      
      // Ensure inquiries is always an array
      const inquiriesData = response.data.data.inquiries || [];
      setInquiries(inquiriesData);
      setTotalPages(response.data.data.pagination.total_pages);
      setError(null);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to fetch inquiries');
      setInquiries([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const handleStatusUpdate = async () => {
    if (!selectedInquiry) return;
    
    try {
      await api.put(`/api/inquiry/general/${selectedInquiry.id}/status`, {
        status: newStatus,
      } as StatusUpdateRequest);
      
      setStatusDialogOpen(false);
      fetchInquiries(); // Refresh the list
    } catch (err) {
      setError('Failed to update inquiry status');
      console.error('Error updating status:', err);
    }
  };

  const openStatusDialog = (inquiry: GeneralInquiry) => {
    setSelectedInquiry(inquiry);
    setNewStatus(inquiry.status);
    setStatusDialogOpen(true);
  };

  const openViewDialog = (inquiry: GeneralInquiry) => {
    setViewInquiry(inquiry);
    setViewDialogOpen(true);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        General Inquiries
      </Typography>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        General inquiries (excluding OAPI related inquiries)
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search and Filter Bar */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          sx={{ flexGrow: 1 }}
          variant="outlined"
          placeholder="Search general inquiries..."
          value={searchTerm}
          onChange={handleSearch}
          onKeyPress={handleSearchKeyPress}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: (
              <IconButton 
                onClick={handleSearchSubmit}
                size="small"
                sx={{ mr: 1 }}
                title="Search"
              >
                <SearchIcon />
              </IconButton>
            ),
          }}
        />
        
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Status Filter</InputLabel>
          <Select
            value={statusFilter === -1 ? 'all' : statusFilter.toString()}
            onChange={handleStatusFilterChange}
            label="Status Filter"
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value={0}>Pending</MenuItem>
            <MenuItem value={1}>In Progress</MenuItem>
            <MenuItem value={2}>Completed</MenuItem>
            <MenuItem value={3}>Rejected</MenuItem>
            <MenuItem value={4}>On Hold</MenuItem>
            <MenuItem value={5}>Canceled</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inquiries && inquiries.length > 0 ? (
              inquiries.map((inquiry) => (
                <TableRow key={inquiry.id}>
                  <TableCell>{inquiry.id}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {inquiry.name}
                    </Typography>
                    {inquiry.user_id && (
                      <Typography variant="caption" color="textSecondary">
                        User ID: {inquiry.user_id}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {inquiry.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {inquiry.message || 'No message'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusLabels[inquiry.status as keyof typeof statusLabels]?.label}
                      color={statusLabels[inquiry.status as keyof typeof statusLabels]?.color}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(inquiry.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => openViewDialog(inquiry)} size="small">
                      <ViewIcon />
                    </IconButton>
                    <IconButton onClick={() => openStatusDialog(inquiry)} size="small" title="Update Status">
                      <SettingsIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {error ? `Error: ${error}` : 'No inquiries found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)}>
        <DialogTitle>Update Inquiry Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value as number)}
              label="Status"
            >
              <MenuItem value={0}>Pending</MenuItem>
              <MenuItem value={1}>In Progress</MenuItem>
              <MenuItem value={2}>Completed</MenuItem>
              <MenuItem value={3}>Rejected</MenuItem>
              <MenuItem value={4}>On Hold</MenuItem>
              <MenuItem value={5}>Canceled</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleStatusUpdate} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Inquiry Details</DialogTitle>
        <DialogContent>
          {viewInquiry && (
            <Box>
              <Typography variant="h6" gutterBottom>Contact Information</Typography>
              <Typography><strong>Name:</strong> {viewInquiry.name}</Typography>
              <Typography><strong>Email:</strong> {viewInquiry.email}</Typography>
              {viewInquiry.user_id && (
                <Typography><strong>User ID:</strong> {viewInquiry.user_id}</Typography>
              )}
              
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Message</Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                {viewInquiry.message || 'No message provided'}
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Status Information</Typography>
              <Typography><strong>Status:</strong> {statusLabels[viewInquiry.status as keyof typeof statusLabels]?.label}</Typography>
              <Typography><strong>Created:</strong> {new Date(viewInquiry.created_at).toLocaleString()}</Typography>
              <Typography><strong>Updated:</strong> {new Date(viewInquiry.updated_at).toLocaleString()}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GeneralInquiryTab;
